import axios from 'axios'

/**
 * Deux bases parce que api.fishfriender.com réécrit `/{path}` en
 * `/api/{path}`, sauf pour les préfixes qu'il laisse passer tels quels — dont
 * `/admin`. Les routes montées sous `/api` s'appellent donc sans ce préfixe,
 * celles du back-office avec leur chemin complet. En production les deux
 * pointent sur le même hôte ; en local, Express sert tout à la racine.
 */
const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_FF_API,
  withCredentials: true,
})

const adminAxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_FF_API_ROOT,
  withCredentials: true,
})

export const api = {
  /**
   * La stratégie `admins` de l'API refuse un compte sans rôle de back-office,
   * et les routes de support exigent la permission `support` (rôles `SUPPORT`
   * ou `ADMIN`). La session s'ouvre sur l'hôte de l'API, d'où les appels
   * ultérieurs.
   */
  login: async ({
    username,
    password,
  }: {
    username: string
    password: string
  }) => {
    await adminAxiosInstance.post('/admin/api/login', { username, password })
  },
  logout: async () => {
    await adminAxiosInstance.get('/admin/api/logout')
  },
  getUserMe: async () => {
    return (await axiosInstance.get('/users/me')).data
  },
  listChannels: async ({
    status = 'active',
    query,
  }: {
    status?: 'active' | 'archived' | 'all'
    query?: string
  }): Promise<Api_Channels.Channel[]> => {
    return (
      await adminAxiosInstance.get<Api_Channels.Channel[]>(
        '/admin/api/support/channels',
        {
          params: { status, query },
        },
      )
    ).data
  },
  getChannelDetails: async ({
    channelId,
  }: {
    channelId: string
  }): Promise<Api_ChannelsDetails.RootObject> => {
    return (
      await adminAxiosInstance.get<Api_ChannelsDetails.RootObject>(
        `/admin/api/support/channels/${channelId}`,
      )
    ).data
  },
  supportDatabaseDownloadUrl: (messageId: string) =>
    `${
      process.env.REACT_APP_FF_API_ROOT
    }/admin/api/support/messages/${encodeURIComponent(messageId)}/database`,
  putChannel: async (
    channelId: string,
    body: Partial<{ status: 'active' | 'archived' }>,
  ) => {
    return (
      await adminAxiosInstance.put(
        `/admin/api/support/channels/${channelId}`,
        body,
      )
    ).data
  },
  listMessages: async ({
    channelId,
    after,
    before,
  }: {
    channelId: string
    after?: Date
    before?: Date
  }) => {
    return (
      await axiosInstance.get<Api_Chat.ChatMessage[]>(
        `/chat/channels/${channelId}/messages`,
        {
          params: {
            after: after?.getTime(),
            before: before?.getTime(),
          },
        },
      )
    ).data
  },
}
